import { useMemo } from 'react';
import { useQuery } from 'urql';

import { FeatureFlag } from '@revelio/core';
import {
  SKILL_TAXONOMIES_VERSIONS_QUERY,
  type SkillTaxonomiesVersionsQuery,
  TaxonomyType,
} from '@revelio/data-access';

import type { CustomSkillTaxonomySelection } from '../../../next/CustomDash/filters/definitions/skillTaxonomy';
import { useUnleashFlag } from '../../unleash-flags/use-unleash-flag';
import { useTaxonomyList } from './useTaxonomyList';
import { useTaxonomyLatestVersion } from './useTaxonomyVersions';

type SkillTaxonomy = NonNullable<
  SkillTaxonomiesVersionsQuery['selectionList']
>['skill_taxonomies']['taxs'][0];

export const useSkillTaxonomyVersions = () => {
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);
  const [{ data: legacyData, fetching: legacyFetching, error: legacyError }] =
    useQuery({
      query: SKILL_TAXONOMIES_VERSIONS_QUERY,
      pause: useReferenceApi,
    });
  const {
    taxonomies: referenceTaxonomies,
    hasData: hasReferenceTaxonomies,
    fetching: referenceFetching,
    error: referenceError,
  } = useTaxonomyList({
    taxonomyType: TaxonomyType.Skill,
    pause: !useReferenceApi,
  });
  const {
    version: latestReferenceVersion,
    hasData: hasLatestReferenceVersion,
    fetching: latestReferenceFetching,
    error: latestReferenceError,
  } = useTaxonomyLatestVersion({
    taxonomyType: TaxonomyType.Skill,
    pause: !useReferenceApi,
  });

  const mappedReferenceTaxonomies = useMemo(() => {
    if (!hasReferenceTaxonomies || !hasLatestReferenceVersion) {
      return undefined;
    }

    return referenceTaxonomies.map(
      (taxonomy): SkillTaxonomy => ({
        id: taxonomy.id,
        name: taxonomy.name,
        version: taxonomy.version,
        is_latest:
          latestReferenceVersion !== undefined &&
          latestReferenceVersion !== null &&
          taxonomy.version === latestReferenceVersion,
      })
    );
  }, [
    hasLatestReferenceVersion,
    hasReferenceTaxonomies,
    latestReferenceVersion,
    referenceTaxonomies,
  ]);

  if (useReferenceApi) {
    return {
      taxonomies: mappedReferenceTaxonomies,
      loading:
        referenceFetching ||
        latestReferenceFetching ||
        (!hasReferenceTaxonomies && !referenceError) ||
        (!hasLatestReferenceVersion && !latestReferenceError),
      error: referenceError ?? latestReferenceError,
    };
  }

  return {
    taxonomies: legacyData?.selectionList?.skill_taxonomies?.taxs,
    loading: legacyFetching,
    error: legacyError,
  };
};

export const useCurrentSkillTaxonomyVersion = () => {
  const { taxonomies, loading, error } = useSkillTaxonomyVersions();

  const defaultVersion = (() => {
    const latestDefaultTaxonomy = taxonomies?.find(
      (taxonomy: SkillTaxonomy) => taxonomy.is_latest
    );
    if (latestDefaultTaxonomy) return latestDefaultTaxonomy.version;

    return undefined;
  })();

  const getSpecificTaxonomyVersion = (
    taxonomySelection: CustomSkillTaxonomySelection
  ) => {
    if (taxonomySelection.taxonomyId) {
      const taxonomy = taxonomies?.find(
        (taxonomy: SkillTaxonomy) =>
          taxonomy.id === taxonomySelection.taxonomyId
      );
      return taxonomy?.version;
    }

    return defaultVersion;
  };

  return {
    version: defaultVersion,
    taxonomies,
    loading: loading || (!taxonomies && !error),
    error,
    isValidTaxonomyId: (taxonomyId: number) => {
      if (error) {
        console.error('Taxonomy versions are unknown', error);
        return false;
      }
      return (
        taxonomies?.some(
          (taxonomy: SkillTaxonomy) => taxonomy.id === taxonomyId
        ) ?? false
      );
    },
    getSpecificTaxonomyVersion,
  };
};
